import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import {
  InsuranceFlowDetailsResponse,
  InsuranceFlowFullHistoryElement,
  InsuranceFlowLeadSnapshotResponse,
  InsuranceFlowProposalSnapshotResponse,
} from 'src/app/_types/insurance-flow.types';
import { Lead } from 'src/app/_types/lead.types';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';

import { TaskDetailsService } from '../../task-details.service';
import { TaskCancellationsService } from '../task-cancellations/task-cancellations.service';
import { TaskLogCancellationChangesModalComponent } from './task-log-cancellation-changes-modal.component';
import { TaskLogDiffModalData } from './task-log-diff.types';
import { TaskLogDiffModalComponent } from './task-log-diff-modal/task-log-diff-modal.component';
import {
  TaskLogRow,
  TaskLogTableComponent,
} from './task-log-table/task-log-table.component';

type TaskLogGroup = {
  flowId: string;
  displayId: string;
  /** The flow's own entries, newest first: where the diff baselines are found. */
  elements: InsuranceFlowFullHistoryElement[];
  /** Those and the timelines of the cancellations ending the flow, newest first. */
  rows: TaskLogRow[];
  /** The flow's creation: its oldest own row. Cancellation rows do not count. */
  createdOn: number;
};

const time = (date: string | null | undefined): number =>
  new Date(date ?? 0).getTime();

@Component({
  selector: 'app-task-log',
  standalone: true,
  imports: [
    CommonModule,
    ExpansionPanelComponent,
    TaskLogTableComponent,
    TaskLogDiffModalComponent,
    TaskLogCancellationChangesModalComponent,
  ],
  templateUrl: './task-log.component.html',
})
export class TaskLogComponent implements OnInit, OnDestroy {
  @ViewChild(TaskLogDiffModalComponent)
  private readonly diffModal?: TaskLogDiffModalComponent;

  @ViewChild(TaskLogCancellationChangesModalComponent)
  private readonly changesModal?: TaskLogCancellationChangesModalComponent;

  protected logsList: InsuranceFlowFullHistoryElement[] = [];

  protected groupedLogs: TaskLogGroup[] = [];

  protected selectedDiffData: TaskLogDiffModalData | null = null;

  private insuranceFlowData: InsuranceFlowDetailsResponse | null = null;

  /** The affaire's cancellations, merged into their base flow's group. */
  private cancellations: CancellationFlowDetailsResponse[] = [];

  private stateSubscription?: Subscription;

  private cancellationsSubscription?: Subscription;

  private previousIsLoading = true;

  constructor(
    private readonly taskDetailsService: TaskDetailsService,
    private readonly insuranceFlowService: InsuranceFlowService,
    private readonly taskCancellationsService: TaskCancellationsService
  ) {}

  ngOnInit(): void {
    this.stateSubscription = this.taskDetailsService.state$.subscribe(
      (state) => {
        this.insuranceFlowData = state.insuranceFlowData;
        const flowId = state.insuranceFlowData?.id;
        const shouldReloadLogs = this.previousIsLoading && !state.isLoading;
        this.previousIsLoading = state.isLoading;

        if (!flowId || !shouldReloadLogs) {
          return;
        }

        this.fetchLogs(flowId);
      }
    );
    // Re-read after every cancellation action, so a new entry shows at once.
    this.cancellationsSubscription =
      this.taskCancellationsService.cancellations$.subscribe(
        (cancellations) => {
          this.cancellations = cancellations;
          this.groupedLogs = this.buildGroupedLogs(this.logsList);
        }
      );
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
    this.cancellationsSubscription?.unsubscribe();
  }

  protected get hasLogs(): boolean {
    return this.groupedLogs.length > 0;
  }

  /** The table only hands up the rows it shows as clickable. */
  protected onEntryClicked(
    groupElements: InsuranceFlowFullHistoryElement[],
    currentElement: TaskLogRow
  ): void {
    if (currentElement.kind === 'cancellation') {
      this.changesModal?.openModal(currentElement);
      return;
    }

    if (!currentElement.leadSnapshot && !currentElement.proposalSnapshot) {
      return;
    }

    const leadBaseline = this.resolveLeadBaseline(
      groupElements,
      currentElement
    );
    const proposalBaseline = this.resolveProposalBaseline(
      groupElements,
      currentElement
    );

    this.selectedDiffData = {
      currentElement,
      leadBaseline,
      proposalBaseline,
    };

    this.diffModal?.openModal(this.selectedDiffData);
  }

  protected closeDiffModal(): void {
    this.selectedDiffData = null;
  }

  private fetchLogs(flowId: string): void {
    this.logsList = [];
    this.groupedLogs = [];

    this.insuranceFlowService.getFlowFullHistory(flowId).subscribe({
      next: (response) => {
        const fullHistoryList = response?.data?.history ?? [];
        this.logsList = fullHistoryList;
        this.groupedLogs = this.buildGroupedLogs(fullHistoryList);
      },
      error: () => {
        this.logsList = [];
        this.groupedLogs = [];
      },
    });
  }

  private buildGroupedLogs(
    logsList: InsuranceFlowFullHistoryElement[]
  ): TaskLogGroup[] {
    const groups = new Map<string, InsuranceFlowFullHistoryElement[]>();

    for (const element of logsList) {
      const existing = groups.get(element.flowId);
      if (existing) {
        existing.push(element);
      } else {
        groups.set(element.flowId, [element]);
      }
    }

    const grouped = Array.from(groups.entries()).map(([flowId, elements]) => {
      const sortedElements = [...elements].sort(
        (a, b) => time(b.updatedOn) - time(a.updatedOn)
      );
      // A cancellation with no base flow, or whose base has no group, is not shown.
      const rows: TaskLogRow[] = [
        ...sortedElements.map((element) => ({
          ...element,
          kind: 'flow' as const,
        })),
        ...this.cancellations
          .filter(
            (cancellation) => cancellation.baseInsuranceFlow?.id === flowId
          )
          .flatMap((cancellation) =>
            cancellation.timeline.map((entry) => ({
              ...entry,
              kind: 'cancellation' as const,
              displayId: cancellation.displayId,
            }))
          ),
      ].sort((a, b) => time(b.updatedOn) - time(a.updatedOn));

      return {
        flowId,
        displayId: sortedElements[0]?.displayId ?? flowId,
        elements: sortedElements,
        rows,
        createdOn: time(sortedElements[sortedElements.length - 1]?.updatedOn),
      };
    });

    // Newest flow first: an older flow's later activity does not lift it.
    return grouped.sort((a, b) => b.createdOn - a.createdOn);
  }

  // Group elements are sorted from most recent to least recent.
  // Baselines are looked up in previous indexes because they are more recent values.
  private resolveLeadBaseline(
    groupElements: InsuranceFlowFullHistoryElement[],
    currentElement: InsuranceFlowFullHistoryElement
  ): InsuranceFlowLeadSnapshotResponse | Lead | null {
    const currentLeadId = currentElement.leadSnapshot?.leadId;
    if (!currentLeadId) {
      return null;
    }

    const currentIndex = groupElements.findIndex(
      (element) => element.id === currentElement.id
    );
    if (currentIndex !== -1) {
      for (let index = currentIndex - 1; index >= 0; index -= 1) {
        const candidate = groupElements[index]?.leadSnapshot;
        if (candidate?.leadId === currentLeadId) {
          return candidate;
        }
      }
    }

    const finalLead = this.insuranceFlowData?.lead;
    if (finalLead?.leadId === currentLeadId) {
      return finalLead;
    }

    return null;
  }

  private resolveProposalBaseline(
    groupElements: InsuranceFlowFullHistoryElement[],
    currentElement: InsuranceFlowFullHistoryElement
  ): InsuranceFlowProposalSnapshotResponse | ProposalDetails | null {
    const currentProposalId = currentElement.proposalSnapshot?.proposalId;
    if (!currentProposalId) {
      return null;
    }

    const currentIndex = groupElements.findIndex(
      (element) => element.id === currentElement.id
    );
    if (currentIndex !== -1) {
      for (let index = currentIndex - 1; index >= 0; index -= 1) {
        const candidate = groupElements[index]?.proposalSnapshot;
        if (candidate?.proposalId === currentProposalId) {
          return candidate;
        }
      }
    }

    const proposalList = this.insuranceFlowData?.proposals ?? [];
    const policy = this.insuranceFlowData?.policy
      ? [this.insuranceFlowData.policy]
      : [];
    const finalProposal = [...proposalList, ...policy].find(
      (proposal) => proposal?.proposalId === currentProposalId
    );

    return finalProposal ?? null;
  }
}
